import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import AddVideoDialog from "@/components/library/AddVideoDialog";
import VideoCard from "@/components/library/VideoCard";
import FolderBar from "@/components/library/FolderBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search as SearchIcon, Library as LibraryIcon } from "lucide-react";

const STATUS_TABS = [
  { value: "all", label: "Все" },
  { value: "not_started", label: "Не начато" },
  { value: "learning", label: "Изучается" },
  { value: "completed", label: "Завершено" },
];

export default function Library() {
  const [addOpen, setAddOpen] = useState(false);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("all");

  const { data: folders = [] } = useQuery({
    queryKey: ["folders"],
    queryFn: async () => (await api.get("/folders")).data,
  });

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ["videos", status, search, folder],
    queryFn: async () => {
      const params = {};
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      if (folder !== "all") params.folder_id = folder;
      const { data } = await api.get("/videos", { params });
      return data;
    },
  });

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="library-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Библиотека</h1>
          <p className="text-sm text-muted-foreground mt-1">Ваши видео для изучения языка через контекст</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-video-button">
          <Plus className="w-4 h-4 mr-1" /> Добавить видео
        </Button>
      </div>

      <FolderBar folders={folders} activeFolder={folder} onSelect={setFolder} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList data-testid="library-status-tabs">
            {STATUS_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`library-status-tab-${t.value}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Поиск видео..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="library-search-input"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Загрузка...</div>
      ) : videos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3" data-testid="library-empty-state">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
            <LibraryIcon className="w-7 h-7 text-muted-foreground" />
          </div>
          <p className="font-medium">Пока нет видео</p>
          <p className="text-sm text-muted-foreground max-w-xs">Вставьте ссылку на YouTube видео, чтобы начать изучение языка через контекст.</p>
          <Button onClick={() => setAddOpen(true)} data-testid="add-video-empty-button">
            <Plus className="w-4 h-4 mr-1" /> Добавить первое видео
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="video-grid">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} folders={folders} />
          ))}
        </div>
      )}

      <AddVideoDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
